import { useAdminClinic, useAdminInfiniteClinicsList } from '@/hooks/admin/useAdminClinics';
import { useAdminClinicInformationsList } from '@/hooks/admin/useAdminClinicInformations';
import { useTenantSiteTypes } from '@/hooks/admin/useTenantSiteTypes';
import { useAddressSearch, usePrefectures } from '@/hooks/useAddressSearch';
import { useAddressSearchModalHelper } from '@/hooks/useAddressSearchModalHelper';
import { useJapaneseNameKanaFields } from '@/hooks/useJapaneseNameKanaFields';
import { CLINIC_INFORMATION_CLINIC_ID_NOT_EXISTS_API_MESSAGE } from '@/pages/admin/site-admin-clinic-info/applyClinicInformationApiErrors';
import { ClinicCreateFormData } from '@/pages/admin/site-admin-clinic-info/clinicFormSchema';
import { convertSeirekiWrap } from '@/utils/wareki';
import {
  AppAdminFormLabel,
  AppButton,
  AppCheckbox,
  AppSelect,
  AppSpin,
  AppTimePicker,
} from '@components/atoms';
import { TextFieldWithLabel } from '@components/molecules';
import dayjs, { type Dayjs } from '@shared/lib/dayjs';
import { CheckboxOptionType } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router-dom';
import AddressSearchModal from './AddressSearchModal';
import AuthorizedReceiptForm from './AuthorizedReceiptForm';
import ClinicPaymentInfoForm from './ClinicPaymentInfoForm';
import OtherInfoForm from './OtherInfoForm';
import RA2NDatePicker from './RA2NDatePicker/RA2NDatePicker';
import ReceiptFeeTransferForm from './ReceiptFeeTransferForm';
import { normalizePostalCodeInput } from '@/utils';

export type LatestAvailableToGetter = () => string | null;

export type AdjacentPeriodCheckResult = {
  fromError: string | null;
  toError: string | null;
};
export type AdjacentPeriodChecker = (
  subNumber: number,
  availableFrom: string,
  availableTo: string | null | undefined
) => AdjacentPeriodCheckResult;

interface ClinicInfoCreateFormProps {
  clinicId?: string;
  isEdit?: boolean;
  isCopy?: boolean;
  onRegisterLatestAvailableToGetter?: (fn: LatestAvailableToGetter | null) => void;
  onRegisterAdjacentPeriodChecker?: (fn: AdjacentPeriodChecker | null) => void;
}

const ClinicInfoCreateForm = ({
  clinicId,
  isEdit = false,
  isCopy = false,
  onRegisterLatestAvailableToGetter,
  onRegisterAdjacentPeriodChecker,
}: ClinicInfoCreateFormProps) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<ClinicCreateFormData>();
  const { site } = useParams<{ site: string; id: string }>();
  const [searchParams] = useSearchParams();
  const urlClinicId = searchParams.get('clinic_id');
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
  const [selectedClinicSite, setSelectedClinicSite] = useState<string | null>(null);
  const [isClinicIdDisabled, setIsClinicIdDisabled] = useState(false);

  const { data: tenants } = useTenantSiteTypes();

  const apiSite = site || 'medix';

  const filterSiteTypes = useMemo(() => {
    if (site === 'super-admin') {
      return tenants?.data?.site_types?.map(s => s.site_type) || [];
    }
    if (site === 'medix') {
      return ['medix', 'upsystem'];
    }
    return site ? [site] : ['medix'];
  }, [site, tenants?.data?.site_types]);

  const { data: prefectures } = usePrefectures({
    siteType: site || 'medix',
    isAdmin: true,
  });
  const clinicInfo = watch('clinicInfo');
  const address1 = useWatch({
    control,
    name: 'clinicInfo.address1',
  });
  const watchedFormClinicId = useWatch({
    control,
    name: 'clinicInfo.clinic_id',
  });

  const { data: clinicData, status: clinicInfoStatus } = useAdminClinic(
    apiSite,
    Number(urlClinicId || clinicId) || 0
  );

  const {
    data: clinicsListData,
    isLoading: isClinicsListLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAdminInfiniteClinicsList(apiSite, {
    site_types: filterSiteTypes,
    sort: ['created_at', 'asc'],
  });

  const options: CheckboxOptionType<number>[] = [
    { label: '日', value: 0 },
    { label: '月', value: 1 },
    { label: '火', value: 2 },
    { label: '水', value: 3 },
    { label: '木', value: 4 },
    { label: '金', value: 5 },
    { label: '土', value: 6 },
  ];

  const allClinics = useMemo(() => {
    return clinicsListData?.pages.flatMap(page => page.data.data) || [];
  }, [clinicsListData?.pages]);

  const selectedClinicNumber = useMemo(() => {
    if (!selectedClinicId) return undefined;
    const fromList = allClinics.find(c => c.id === selectedClinicId)?.clinic_number;
    if (fromList) return fromList;
    if (clinicData?.data?.id === selectedClinicId)
      return clinicData.data.clinic_number ?? undefined;
    return undefined;
  }, [selectedClinicId, allClinics, clinicData]);

  const { data: existingClinicInfosData } = useAdminClinicInformationsList(
    selectedClinicNumber ? apiSite : null,
    { clinic_number: selectedClinicNumber, per_page: 200 }
  );

  const existingClinicInfos = useMemo(() => {
    if (!selectedClinicNumber) return [];
    return (existingClinicInfosData?.data?.data ?? []).filter(
      r => r.clinic_number === selectedClinicNumber
    );
  }, [existingClinicInfosData, selectedClinicNumber]);

  const getLatestAvailableTo = useCallback((): string | null => {
    if (!existingClinicInfos.length) return null;
    const latest = existingClinicInfos.reduce((max, r) =>
      r.sub_number > max.sub_number ? r : max
    , existingClinicInfos[0]);
    return latest.available_to ?? null;
  }, [existingClinicInfos]);

  useEffect(() => {
    onRegisterLatestAvailableToGetter?.(getLatestAvailableTo);
    return () => {
      onRegisterLatestAvailableToGetter?.(null);
    };
  }, [getLatestAvailableTo, onRegisterLatestAvailableToGetter]);

  const checkAdjacentPeriods = useCallback(
    (subNumber: number, availableFrom: string, availableTo: string | null | undefined): AdjacentPeriodCheckResult => {
      const result: AdjacentPeriodCheckResult = { fromError: null, toError: null };
      if (!existingClinicInfos.length) return result;

      const sorted = [...existingClinicInfos].sort((a, b) => a.sub_number - b.sub_number);

      // Previous record: largest sub_number < current
      const prev = sorted.filter(r => r.sub_number < subNumber).pop();
      if (prev?.available_to && availableFrom <= prev.available_to) {
        result.fromError = '前後の有効期間と重複しています。期間を確認してください。';
      }

      // Next record: smallest sub_number > current
      const next = sorted.find(r => r.sub_number > subNumber);
      if (availableTo && next?.available_from && availableTo >= next.available_from) {
        result.toError = '前後の有効期間と重複しています。期間を確認してください。';
      }

      return result;
    },
    [existingClinicInfos]
  );

  useEffect(() => {
    onRegisterAdjacentPeriodChecker?.(checkAdjacentPeriods);
    return () => {
      onRegisterAdjacentPeriodChecker?.(null);
    };
  }, [checkAdjacentPeriods, onRegisterAdjacentPeriodChecker]);

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 10 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const clinicOption = useMemo(() => {
    const list = allClinics.map(clinic => ({
      label: clinic.clinic_number,
      value: clinic.id,
    }));
    const selectedIdFromQuery = Number(urlClinicId || clinicId);
    const selectedId =
      selectedClinicId ?? (Number.isFinite(selectedIdFromQuery) ? selectedIdFromQuery : 0);
    const selectedClinicFromShow = clinicData?.data;
    if (
      selectedId > 0 &&
      selectedClinicFromShow?.id === selectedId &&
      !list.some(option => option.value === selectedId)
    ) {
      list.unshift({
        label: selectedClinicFromShow.clinic_number,
        value: selectedClinicFromShow.id,
      });
    }
    return list;
  }, [allClinics, selectedClinicId, clinicData, urlClinicId, clinicId]);

  const effectiveClinicId = clinicId || urlClinicId;
  /** Lock dropdown to a concrete clinic from URL/props — not route `id` (clinic_information PK). */
  const isLockedMode = !!effectiveClinicId;
  const isEditOrCopy = isEdit || isCopy;
  const hasInitializedDefault = useRef(false);
  const clinicNameKanaWatch = watch('clinicInfo.name_kana');

  useEffect(() => {
    const expectedFetchClinicId = Number(urlClinicId || clinicId) || 0;
    const hasClinicIdNotExistsServerError =
      errors.clinicInfo?.clinic_id?.type === 'server' &&
      String(errors.clinicInfo?.clinic_id?.message ?? '').trim() ===
        CLINIC_INFORMATION_CLINIC_ID_NOT_EXISTS_API_MESSAGE;

    if (isLockedMode) {
      if (clinicInfoStatus === 'success' && clinicData?.data?.id && expectedFetchClinicId > 0) {
        if (hasClinicIdNotExistsServerError) {
          return;
        }
        if (clinicData.data.id !== expectedFetchClinicId) {
          return;
        }
        const formClinicId = Number(watchedFormClinicId) || 0;
        if (formClinicId > 0 && formClinicId !== expectedFetchClinicId) {
          setIsClinicIdDisabled(false);
          return;
        }
        setSelectedClinicId(clinicData.data.id);
        setSelectedClinicSite(clinicData.data.site_type ?? null);
        setIsClinicIdDisabled(true);
        setValue('clinicInfo.clinic_id', clinicData.data.id);
        hasInitializedDefault.current = true;
      } else if (clinicInfoStatus === 'error') {
        setIsClinicIdDisabled(false);
        if (!isClinicsListLoading && allClinics.length > 0) {
          const formClinicId = Number(watchedFormClinicId) || 0;
          const inList = formClinicId > 0 && allClinics.some(c => c.id === formClinicId);
          const stillOnMissingFetchedClinic =
            expectedFetchClinicId > 0 && formClinicId === expectedFetchClinicId;
          const shouldFallbackToFirstClinic =
            formClinicId === 0 || !inList || stillOnMissingFetchedClinic;

          if (shouldFallbackToFirstClinic) {
            const firstId = allClinics[0].id;
            const firstSiteType = allClinics[0].site_type ?? null;
            setSelectedClinicId(firstId);
            setSelectedClinicSite(firstSiteType);
            setValue('clinicInfo.clinic_id', firstId);
            hasInitializedDefault.current = true;
          }
        } else {
          setSelectedClinicId(null);
          setSelectedClinicSite(null);
          setValue('clinicInfo.clinic_id', 0);
          hasInitializedDefault.current = false;
        }
      }
      return;
    }

    setIsClinicIdDisabled(false);
    const shouldDefaultFirstClinicOnCreate = !isEditOrCopy && !effectiveClinicId;
    if (
      shouldDefaultFirstClinicOnCreate &&
      !hasInitializedDefault.current &&
      !isClinicsListLoading &&
      allClinics.length > 0
    ) {
      const firstId = allClinics[0].id;
      const firstSiteType = allClinics[0].site_type ?? null;
      setSelectedClinicId(firstId);
      setSelectedClinicSite(firstSiteType);
      setValue('clinicInfo.clinic_id', firstId);
      hasInitializedDefault.current = true;
    }
  }, [
    isLockedMode,
    isEditOrCopy,
    effectiveClinicId,
    clinicInfoStatus,
    clinicData,
    errors.clinicInfo?.clinic_id,
    watchedFormClinicId,
    isClinicsListLoading,
    allClinics,
    setValue,
    urlClinicId,
    clinicId,
  ]);

  const addressSearchMutation = useAddressSearch({
    siteType: site || 'medix',
    isAdmin: true,
  });

  const clinicNameKanaFields = useJapaneseNameKanaFields({
    nameKanaValue: String(clinicNameKanaWatch ?? ''),
    setNameValue: v => setValue('clinicInfo.name', v, { shouldDirty: true, shouldValidate: false }),
    setKanaValue: v =>
      setValue('clinicInfo.name_kana', v, { shouldDirty: true, shouldValidate: false }),
    maxLength: 255,
    fullWidthAlphaNumOnBlur: false,
  });

  const handleBusinessDaysChange = (checkedValues: number[]) => {
    setValue('clinicInfo.open_week', checkedValues);
  };

  const {
    isAddressModalOpen,
    closeAddressModal,
    handleAddressButtonClick,
    handleAddressSelect,
    modalInput,
  } = useAddressSearchModalHelper<ClinicCreateFormData>({
    postalCodeValue: String(clinicInfo.postal_code ?? ''),
    addressValue: String(address1 ?? ''),
    prefectures,
    setValue,
    fieldNames: {
      postalCode: 'clinicInfo.postal_code',
      address: 'clinicInfo.address1',
      prefectureId: 'clinicInfo.prefecture_id',
    },
    addressSearchMutation,
  });

  const formatDateValue = (val: string | Dayjs | null | undefined) => {
    if (!val) return '';
    const d = dayjs(val);
    return d.isValid() ? d.format('YYYY-MM-DD') : (val as string);
  };

  const showClinicIdFieldError =
    !!errors.clinicInfo?.clinic_id &&
    !(
      isEdit &&
      errors.clinicInfo?.clinic_id?.type === 'server' &&
      String(errors.clinicInfo?.clinic_id?.message ?? '').trim() ===
        CLINIC_INFORMATION_CLINIC_ID_NOT_EXISTS_API_MESSAGE
    );

  return (
    <div>
      <div className="my-6">
        <AppAdminFormLabel label="治療院ID" required />
        <Controller
          name="clinicInfo.clinic_id"
          control={control}
          render={({ field }) => (
            <AppSelect
              {...field}
              style={{ width: '100%' }}
              value={selectedClinicId || undefined}
              onChange={value => {
                const clinicId = value ? Number(value) : 0;
                field.onChange(clinicId);
                setSelectedClinicId(clinicId > 0 ? clinicId : null);
                if (clinicId > 0) {
                  const selectedClinic = allClinics.find(c => c.id === clinicId);
                  setSelectedClinicSite(selectedClinic?.site_type ?? null);
                } else {
                  setSelectedClinicSite(null);
                }
              }}
              disabled={isClinicIdDisabled || isClinicsListLoading}
              loading={isClinicsListLoading}
              options={clinicOption}
              onPopupScroll={handlePopupScroll}
              popupRender={menu => (
                <>
                  {menu}
                  {isFetchingNextPage && (
                    <div className="p-4 text-center">
                      <AppSpin />
                    </div>
                  )}
                </>
              )}
            />
          )}
        />
        {showClinicIdFieldError && (
          <p className="text-red-500 text-sm mt-1">{errors.clinicInfo?.clinic_id?.message}</p>
        )}
        {isClinicIdDisabled && selectedClinicId && clinicData?.data?.name && (
          <p className="text-gray-600 text-sm mt-2">{clinicData.data.name}</p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-8 mt-4 gap-y-4">
        <Controller
          name="clinicInfo.name"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="治療院名"
              required
              {...field}
              {...clinicNameKanaFields.nameInputProps}
              error={errors.clinicInfo?.name}
            />
          )}
        />
        <Controller
          name="clinicInfo.name_kana"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="治療院カナ"
              {...field}
              {...clinicNameKanaFields.kanaInputProps}
              error={errors.clinicInfo?.name_kana}
            />
          )}
        />

        <div>
          <AppAdminFormLabel label="適用期間" required />
          <div className="flex gap-4">
            <div className="flex-1">
              <RA2NDatePicker
                placeholder=""
                className="w-full"
                value={formatDateValue(clinicInfo.available_from)}
                onChange={date => {
                  const seireki = convertSeirekiWrap(date).replace(/\//g, '-');
                  setValue('clinicInfo.available_from', seireki, {
                    shouldValidate: false,
                    shouldDirty: true,
                  });
                }}
                error={!!errors.clinicInfo?.available_from}
              />
            </div>
            <span className="mt-2 whitespace-nowrap">から</span>
            <div className="flex-1">
              <RA2NDatePicker
                className="w-full"
                placeholder=""
                value={formatDateValue(clinicInfo.available_to)}
                startDate={
                  clinicInfo.available_from && dayjs(clinicInfo.available_from).isValid()
                    ? dayjs(clinicInfo.available_from).add(1, 'day').format('YYYY-MM-DD')
                    : undefined
                }
                onChange={date => {
                  const seireki = convertSeirekiWrap(date).replace(/\//g, '-');
                  setValue('clinicInfo.available_to', seireki, {
                    shouldValidate: false,
                    shouldDirty: true,
                  });
                }}
                error={!!errors.clinicInfo?.available_to}
              />
            </div>
          </div>
          {(errors.clinicInfo?.available_from || errors.clinicInfo?.available_to) && (
            <div className="mt-1 space-y-0.5">
              {errors.clinicInfo?.available_from && (
                <p className="text-sm text-red-500">
                  {errors.clinicInfo.available_from.message?.toString()}
                </p>
              )}
              {errors.clinicInfo?.available_to && (
                <p className="text-sm text-red-500">
                  {errors.clinicInfo.available_to.message?.toString()}
                </p>
              )}
            </div>
          )}
        </div>
        <div />
        <div>
          <AppAdminFormLabel label="営業日" />
          <AppCheckbox.Group
            options={options}
            value={clinicInfo.open_week ?? []}
            onChange={handleBusinessDaysChange}
          />
        </div>
        <div>
          <AppAdminFormLabel label="営業時間" />
          <div className="flex items-center gap-4">
            <div className="w-full">
              <AppTimePicker
                placeholder="--:--"
                className="w-full"
                value={clinicInfo.open_time_from as Dayjs}
                onChange={time => setValue('clinicInfo.open_time_from', time as Dayjs)}
              />
            </div>
            <span className="whitespace-nowrap">から</span>
            <div className="w-full">
              <AppTimePicker
                placeholder="--:--"
                className="w-full"
                value={clinicInfo.open_time_to as Dayjs}
                disabledTime={() => {
                  const from = clinicInfo.open_time_from as Dayjs;
                  if (!from) return {};
                  const fromHour = from.hour();
                  const fromMinute = from.minute();
                  return {
                    disabledHours: () => Array.from({ length: fromHour }, (_, i) => i),
                    disabledMinutes: h => {
                      if (h === fromHour) {
                        return Array.from({ length: fromMinute + 1 }, (_, i) => i);
                      }
                      return [];
                    },
                  };
                }}
                onChange={time => setValue('clinicInfo.open_time_to', time as Dayjs)}
              />
            </div>
          </div>
          <div className="h-6 mt-1">
            {errors.clinicInfo?.open_time_to && (
              <p className="text-sm text-red-500">
                {errors.clinicInfo.open_time_to.message?.toString()}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="grid items-end grid-cols-3 mt-4 gap-y-4 gap-x-5">
        <div className="flex items-center gap-10">
          <Controller
            name="clinicInfo.postal_code"
            control={control}
            render={({ field }) => (
              <TextFieldWithLabel
                label="郵便番号"
                required
                className="flex-1"
                {...field}
                onChange={e => field.onChange(normalizePostalCodeInput(e.target.value))}
                error={errors.clinicInfo?.postal_code}
              />
            )}
          />
          <AppButton type="primary" onClick={handleAddressButtonClick}>
            住所補助
          </AppButton>
        </div>
        <div className="col-span-2" />
        <Controller
          name="clinicInfo.address1"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="住所 "
              required
              {...field}
              value={field.value ?? ''}
              error={errors.clinicInfo?.address1}
            />
          )}
        />
        <Controller
          name="clinicInfo.address2"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label=""
              required
              {...field}
              value={field.value ?? ''}
              error={errors.clinicInfo?.address2}
            />
          )}
        />
        <Controller
          name="clinicInfo.address3"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label=""
              placeholder="建物名・部屋番号"
              {...field}
              value={field.value ?? ''}
              error={errors.clinicInfo?.address3}
            />
          )}
        />
        <Controller
          name="clinicInfo.telephone"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="電話番号"
              required
              {...field}
              value={field.value ?? ''}
              error={errors.clinicInfo?.telephone}
              onChange={e => {
                field.onChange(e);
              }}
            />
          )}
        />
        <Controller
          name="clinicInfo.mobile"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="携帯電話番号"
              required
              {...field}
              value={field.value ?? ''}
              error={errors.clinicInfo?.mobile}
              onChange={e => {
                field.onChange(e);
              }}
            />
          )}
        />
        <Controller
          name="clinicInfo.fax"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="FAX番号"
              {...field}
              value={field.value ?? ''}
              error={errors.clinicInfo?.fax}
            />
          )}
        />
        <Controller
          name="clinicInfo.email"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="メールアドレス"
              {...field}
              value={field.value ?? ''}
              maxLength={320}
              error={errors.clinicInfo?.email}
            />
          )}
        />
      </div>
      <AddressSearchModal
        isOpen={isAddressModalOpen}
        onClose={closeAddressModal}
        onSelect={handleAddressSelect}
        initialPostalCode1={modalInput.initialPostalCode1}
        initialPostalCode2={modalInput.initialPostalCode2}
        isAdmin={true}
        initialAddress={modalInput.initialAddress}
      />
      {selectedClinicSite === 'upsystem' && (
        <>
          <ClinicPaymentInfoForm />
          <AuthorizedReceiptForm />
          <ReceiptFeeTransferForm />
        </>
      )}
      <OtherInfoForm clinicSite={selectedClinicSite ?? 'medix'} />
    </div>
  );
};

export default ClinicInfoCreateForm;
